import { Avatar } from 'primereact/avatar';
import dateFormat from '../../../../utils/date-format';
import React, { useState, useEffect } from 'react';
import { UserSummaryType } from '../../../../api/types/user/user-summary.type';
import { Divider } from 'primereact/divider';
import { UserType } from '../../../../api/types/user/user-type';
import { Image } from 'primereact/image';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Badge } from 'primereact/badge';
import {
  Gender,
  MaritalStatus,
} from '../../../../api/types/user/user-summary.type';
import { toast } from 'react-toastify';
import { isValidEmail } from '../../../../utils/utils-generat';
import { updateUserDetailsOperation } from './operations/update-user-details-operation';
import { ChangePasswordFlow } from './components/password-change/change-password-flow';
import phoneNumberUtils, {
  PhoneNumberComponent,
} from '../../../../utils/international-to-israeli';
import { logoutOperation } from './operations/logout-operation';

interface UserPageProfileModel {
  user: UserSummaryType;
  displayChangePassword: boolean;
}

interface UserFormData {
  firstName: string;
  lastName: string;
  nationalId: string;
  dateOfBirth: Date | null;
  gender: Gender | null;
  maritalStatus: MaritalStatus | null;
  phoneNumber: string;
  email: string;
  city: string;
  street: string;
  houseNumber: string;
  flatNumber: string;
  postcode: string;
  immigrateCountry: string | null;
  immigrateDate: Date | null;
}

interface FieldChange {
  field: keyof UserFormData;
  originalValue: any;
  newValue: any;
}

export const UserPageProfile: React.FC<UserPageProfileModel> = ({
  user,
  displayChangePassword = false,
}) => {
  // Helper to convert international phone format to Israeli display format (054-1233333)
  const convertInternationalToIsraeliDisplay = (
    phoneNumber: string,
  ): string => {
    if (!phoneNumber) return '';

    // Remove all non-digit characters except the leading +
    const cleaned = phoneNumber.replace(/[^\d+]/g, '');

    // If it starts with +972, remove it
    if (cleaned.startsWith('+972')) {
      const digits = cleaned.substring(4); // Remove '+972'
      // Add leading 0 and format as 054-1233333
      if (digits.length >= 2) {
        const formatted = '0' + digits.substring(0, 10);
        if (formatted.length > 3) {
          return formatted.substring(0, 3) + '-' + formatted.substring(3);
        }
        return formatted;
      }
    }

    // If it already starts with 0, just format it
    if (cleaned.startsWith('0')) {
      const digits = cleaned.substring(0, 10);
      if (digits.length > 3) {
        return digits.substring(0, 3) + '-' + digits.substring(3);
      }
      return digits;
    }

    // If it starts with 972 (without +), remove it
    if (cleaned.startsWith('972')) {
      const digits = cleaned.substring(3);
      if (digits.length >= 2) {
        const formatted = '0' + digits.substring(0, 10);
        if (formatted.length > 3) {
          return formatted.substring(0, 3) + '-' + formatted.substring(3);
        }
        return formatted;
      }
    }

    // Return as is if format is not recognized
    return phoneNumber;
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<UserFormData>({
    firstName: user.firstName,
    lastName: user.lastName,
    nationalId: user.nationalId,
    dateOfBirth: user.dateOfBirth ? new Date(user.dateOfBirth) : null,
    gender: user.gender,
    maritalStatus: user.maritalStatus,
    phoneNumber: convertInternationalToIsraeliDisplay(user.phoneNumber),
    email: user.email,
    city: user.city || '',
    street: user.street || '',
    houseNumber: user.houseNumber || '',
    flatNumber: user.flatNumber || '',
    postcode: user.postcode || '',
    immigrateCountry: user.immigrateCountry,
    immigrateDate: user.immigrateDate ? new Date(user.immigrateDate) : null,
  });

  const [originalData] = useState<UserFormData>({ ...formData });
  const [fieldChanges, setFieldChanges] = useState<FieldChange[]>([]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<keyof UserFormData, string>>
  >({});

  // Track field changes
  useEffect(() => {
    const changes: FieldChange[] = [];
    Object.keys(formData).forEach((key) => {
      const field = key as keyof UserFormData;
      if (
        JSON.stringify(formData[field]) !== JSON.stringify(originalData[field])
      ) {
        changes.push({
          field,
          originalValue: originalData[field],
          newValue: formData[field],
        });
      }
    });
    setFieldChanges(changes);
  }, [formData, originalData]);

  // Helper function to check if a field has been changed
  const isFieldChanged = (field: keyof UserFormData): boolean => {
    return fieldChanges.some((change) => change.field === field);
  };

  // Helper function to get only modified fields
  const getModifiedFields = (): Partial<UserFormData> => {
    const modifiedFields: Partial<UserFormData> = {};
    fieldChanges.forEach((change) => {
      modifiedFields[change.field] = change.newValue;
    });
    return modifiedFields;
  };

  // Helper function to reset a specific field
  const resetField = (field: keyof UserFormData) => {
    setFormData((prev) => ({
      ...prev,
      [field]: originalData[field],
    }));
  };

  const genderOptions = [
    { label: 'זכר', value: Gender.Male },
    { label: 'נקבה', value: Gender.Female },
  ];

  const maritalStatusOptions = [
    { label: 'רווק/ה', value: MaritalStatus.Single },
    { label: 'נשוי/ה', value: MaritalStatus.Married },
    { label: 'גרוש/ה', value: MaritalStatus.Divorced },
    { label: 'אלמן/ה', value: MaritalStatus.Widowed },
  ];

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof UserFormData, string>> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'שם פרטי הוא שדה חובה';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'שם משפחה הוא שדה חובה';
    }

    if (!formData.nationalId.trim()) {
      newErrors.nationalId = 'תעודת זהות היא שדה חובה';
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'תאריך לידה הוא שדה חובה';
    }

    if (!formData.gender) {
      newErrors.gender = 'מגדר הוא שדה חובה';
    }

    if (!formData.maritalStatus) {
      newErrors.maritalStatus = 'מצב משפחתי הוא שדה חובה';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'מספר טלפון הוא שדה חובה';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'כתובת אימייל היא שדה חובה';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'כתובת אימייל לא תקינה';
    }

    // Address fields are optional (city, street, houseNumber, flatNumber, postcode)
    // Immigration fields are optional (immigrateCountry, immigrateDate)

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof UserFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Helper to convert empty strings to null for optional fields
  const emptyToNull = (value: string): string | null => {
    return value.trim() === '' ? null : value;
  };

  const handleUserDataSubmit = async () => {
    await updateUserDetailsOperation(
      user.id,
      formData.firstName,
      formData.lastName,
      formData.nationalId,
      formData.phoneNumber,
      formData.phoneNumber,
      formData.email,
      formData.dateOfBirth,
      emptyToNull(formData.city),
      emptyToNull(formData.street),
      emptyToNull(formData.houseNumber),
      emptyToNull(formData.flatNumber),
      emptyToNull(formData.postcode),
      formData.gender,
      formData.immigrateCountry,
      formData.immigrateDate,
      formData.maritalStatus,
    );

    //setShowConfirmationDialog(true);
  };

  const updatePhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');

    // Ensure it starts with '05' and limit to 10 digits
    let formatted = '';

    if (digitsOnly.length > 0) {
      // Ensure the number starts with '05'
      if (!digitsOnly.startsWith('05') && digitsOnly.length >= 2) {
        formatted = '05' + digitsOnly.substring(0, 8);
      } else {
        formatted = digitsOnly.substring(0, 10);
      }

      // Add the hyphen after the third digit if we have more than 3 digits
      if (formatted.length > 3) {
        formatted = formatted.substring(0, 3) + '-' + formatted.substring(3);
      }
    }
    handleInputChange('phoneNumber', formatted);
  };

  const confirmSubmit = async () => {
    try {
      const modifiedFields = getModifiedFields();
      // TODO: Implement API call to update user data with modifiedFields
      toast.success('הפרטים עודכנו בהצלחה');
      setIsEditMode(false);
      setShowConfirmationDialog(false);
    } catch (error) {
      toast.error('שגיאה בעדכון הפרטים');
      console.error('Error updating user data:', error);
    }
  };

  const handleCancel = () => {
    setFormData(originalData);
    setErrors({});
    setIsEditMode(false);
  };

  // Render field with change indicator
  const renderFieldWithIndicator = (
    field: keyof UserFormData,
    label: string,
    content: React.ReactNode,
  ) => {
    const isChanged = isFieldChanged(field);
    return (
      <div style={{ position: 'relative' }}>
        {isChanged && (
          <Badge
            value=""
            severity="info"
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              zIndex: 1,
            }}
          />
        )}
        <label
          style={{
            display: 'block',
            marginBottom: '8px',
            color: '#495057',
            fontWeight: '500',
          }}
        >
          {label}
          {isChanged && (
            <Button
              icon="pi pi-undo"
              className="p-button-text p-button-sm"
              onClick={() => resetField(field)}
              style={{ marginRight: '8px' }}
              tooltip="איפוס לשינוי הקודם"
              tooltipOptions={{ position: 'left' }}
            />
          )}
        </label>
        {content}
      </div>
    );
  };

  // Confirmation dialog footer
  const confirmationDialogFooter = (
    <div>
      <Button
        label="ביטול"
        icon="pi pi-times"
        onClick={() => setShowConfirmationDialog(false)}
        className="p-button-text"
      />
      <Button
        label="אישור"
        icon="pi pi-check"
        onClick={confirmSubmit}
        className="p-button-success"
        autoFocus
      />
    </div>
  );

  // Logout confirmation handler
  const handleLogoutConfirm = async () => {
    await logoutOperation(setLogoutLoading);
  };

  // Logout dialog footer
  const logoutDialogFooter = (
    <div>
      <Button
        label="ביטול"
        icon="pi pi-times"
        onClick={() => setShowLogoutDialog(false)}
        className="p-button-text"
        disabled={logoutLoading}
      />
      <Button
        label="אישור"
        icon="pi pi-check"
        onClick={handleLogoutConfirm}
        className="p-button-danger"
        loading={logoutLoading}
        disabled={logoutLoading}
        autoFocus
      />
    </div>
  );

  return (
    <div
      style={{
        width: '100%',
        padding: '24px',
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid #e9ecef',
        }}
      >
        <div>
          {isEditMode && fieldChanges.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Badge value={fieldChanges.length} severity="info" />
              <span style={{ color: '#495057' }}>שדות שעודכנו</span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          {!isEditMode ? (
            <Button
              icon="pi pi-pencil"
              label="עריכה"
              onClick={() => setIsEditMode(true)}
              className="p-button-text"
              style={{
                color: '#495057',
                padding: '8px 16px',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
              }}
            />
          ) : (
            <>
              <Button
                icon="pi pi-times"
                label="ביטול"
                onClick={handleCancel}
                className="p-button-text"
                style={{
                  color: '#6c757d',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                }}
              />
              <Button
                icon="pi pi-check"
                label="שמירה"
                onClick={handleUserDataSubmit}
                className="p-button-success"
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                }}
              />
            </>
          )}
        </div>
      </div>

      <div
        style={{
          marginTop: '24px',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <h3
          style={{
            color: '#212529',
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '20px',
            paddingBottom: '12px',
            borderBottom: '1px solid #e9ecef',
          }}
        >
          פרטים אישיים
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '250px 1fr',
            gap: '32px',
            alignItems: 'start',
          }}
        >
          {/* Image Section */}
          <div
            style={{
              background: '#f8f9fa',
              borderRadius: '12px',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            }}
          >
            {user.fileUrl ? (
              <Image
                src={user.fileUrl}
                alt={`${user.firstName} ${user.lastName}`}
                width="200"
                preview
                pt={{
                  image: {
                    borderRadius: '12px',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  },
                }}
              />
            ) : (
              <Avatar
                label={user.firstName.charAt(0) + user.lastName.charAt(0)}
                size="xlarge"
                shape="circle"
                style={{
                  width: '200px',
                  height: '200px',
                  fontSize: '4rem',
                  backgroundColor: '#e9ecef',
                  color: '#495057',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                }}
              />
            )}
            <div style={{ textAlign: 'center' }}>
              <h2
                style={{
                  margin: '0',
                  color: '#212529',
                  fontSize: '1.5rem',
                  fontWeight: '600',
                }}
              >
                {isEditMode ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      width: '100%',
                    }}
                  >
                    <InputText
                      value={formData.firstName}
                      onChange={(e) =>
                        handleInputChange('firstName', e.target.value)
                      }
                      className={errors.firstName ? 'p-invalid' : ''}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: errors.firstName
                          ? '1px solid #dc3545'
                          : '1px solid #ced4da',
                        transition: 'all 0.2s ease',
                      }}
                    />
                    <InputText
                      value={formData.lastName}
                      onChange={(e) =>
                        handleInputChange('lastName', e.target.value)
                      }
                      className={errors.lastName ? 'p-invalid' : ''}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: errors.lastName
                          ? '1px solid #dc3545'
                          : '1px solid #ced4da',
                        transition: 'all 0.2s ease',
                      }}
                    />
                  </div>
                ) : (
                  `${user.firstName} ${user.lastName}`
                )}
              </h2>
              <p
                style={{
                  color: '#6c757d',
                  margin: '12px 0 0 0',
                  fontSize: '1rem',
                }}
              >
                {user && user.role === UserType.ADMINISTRATION
                  ? 'מנהלה'
                  : user?.role === UserType.LECTURER
                    ? 'מרצה'
                    : 'סטודנט'}
              </p>
            </div>
          </div>

          {/* Details Section */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              padding: '24px',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <div style={{ display: 'flex', gap: '24px' }}>
                <div style={{ flex: 1 }}>
                  {renderFieldWithIndicator(
                    'nationalId',
                    'תעודת זהות',
                    isEditMode ? (
                      <InputText
                        value={formData.nationalId}
                        onChange={(e) =>
                          handleInputChange('nationalId', e.target.value)
                        }
                        className={errors.nationalId ? 'p-invalid' : ''}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: errors.nationalId
                            ? '1px solid #dc3545'
                            : '1px solid #ced4da',
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#ffffff',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef',
                        }}
                      >
                        {user.nationalId}
                      </div>
                    ),
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  {renderFieldWithIndicator(
                    'dateOfBirth',
                    'תאריך לידה',
                    isEditMode ? (
                      <Calendar
                        value={formData.dateOfBirth}
                        onChange={(e) =>
                          handleInputChange('dateOfBirth', e.value)
                        }
                        dateFormat="dd/mm/yy"
                        className={errors.dateOfBirth ? 'p-invalid' : ''}
                        style={{ width: '100%' }}
                        inputStyle={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: errors.dateOfBirth
                            ? '1px solid #dc3545'
                            : '1px solid #ced4da',
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#ffffff',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef',
                        }}
                      >
                        {dateFormat.displayFormatDate(user.dateOfBirth)}
                      </div>
                    ),
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '24px' }}>
                <div style={{ flex: 1 }}>
                  {renderFieldWithIndicator(
                    'gender',
                    'מגדר',
                    isEditMode ? (
                      <Dropdown
                        value={formData.gender}
                        options={genderOptions}
                        onChange={(e) => handleInputChange('gender', e.value)}
                        className={errors.gender ? 'p-invalid' : ''}
                        style={{ width: '100%' }}
                        pt={{
                          input: {
                            style: {
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: errors.gender
                                ? '1px solid #dc3545'
                                : '1px solid #ced4da',
                              transition: 'all 0.2s ease',
                            },
                          },
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#ffffff',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef',
                        }}
                      >
                        {user.gender === Gender.Male ? 'זכר' : 'נקבה'}
                      </div>
                    ),
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  {renderFieldWithIndicator(
                    'maritalStatus',
                    'מצב משפחתי',
                    isEditMode ? (
                      <Dropdown
                        value={formData.maritalStatus}
                        options={maritalStatusOptions}
                        onChange={(e) =>
                          handleInputChange('maritalStatus', e.value)
                        }
                        className={errors.maritalStatus ? 'p-invalid' : ''}
                        style={{ width: '100%' }}
                        pt={{
                          input: {
                            style: {
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: errors.maritalStatus
                                ? '1px solid #dc3545'
                                : '1px solid #ced4da',
                              transition: 'all 0.2s ease',
                            },
                          },
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#ffffff',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef',
                        }}
                      >
                        {user.maritalStatus === MaritalStatus.Single
                          ? 'רווק/ה'
                          : user.maritalStatus === MaritalStatus.Married
                            ? 'נשוי/ה'
                            : user.maritalStatus === MaritalStatus.Divorced
                              ? 'גרוש/ה'
                              : 'אלמן/ה'}
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Divider style={{ margin: '32px 0' }} />

      <div style={{ marginTop: '24px' }}>
        <h3
          style={{
            color: '#212529',
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '20px',
            paddingBottom: '12px',
            borderBottom: '1px solid #e9ecef',
          }}
        >
          פרטי התקשרות
        </h3>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '24px',
            backgroundColor: '#f8f9fa',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <div style={{ display: 'flex', gap: '24px' }}>
            <div style={{ flex: 1 }}>
              {renderFieldWithIndicator(
                'phoneNumber',
                'טלפון',
                isEditMode ? (
                  <InputText
                    value={formData.phoneNumber}
                    maxLength={11}
                    onChange={(e) => updatePhoneNumber(e.target.value)}
                    className={errors.phoneNumber ? 'p-invalid' : ''}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: errors.phoneNumber
                        ? '1px solid #dc3545'
                        : '1px solid #ced4da',
                      transition: 'all 0.2s ease',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                    }}
                  >
                    {/*{phoneNumberUtils.internationalToIsraeli(user.phoneNumber)}*/}
                    <PhoneNumberComponent phoneNumber={user.phoneNumber} />
                  </div>
                ),
              )}
            </div>
            <div style={{ flex: 1 }}>
              {renderFieldWithIndicator(
                'email',
                'דוא"ל',
                isEditMode ? (
                  <InputText
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={errors.email ? 'p-invalid' : ''}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: errors.email
                        ? '1px solid #dc3545'
                        : '1px solid #ced4da',
                      transition: 'all 0.2s ease',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                    }}
                  >
                    {user.email}
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      </div>

      <Divider style={{ margin: '32px 0' }} />

      <div style={{ marginTop: '24px' }}>
        <h3
          style={{
            color: '#212529',
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '20px',
            paddingBottom: '12px',
            borderBottom: '1px solid #e9ecef',
          }}
        >
          כתובת
        </h3>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '24px',
            padding: '24px',
            backgroundColor: '#f8f9fa',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <div style={{ display: 'flex', gap: '24px', width: '100%' }}>
            <div style={{ flex: 1 }}>
              {renderFieldWithIndicator(
                'city',
                'עיר',
                isEditMode ? (
                  <InputText
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className={errors.city ? 'p-invalid' : ''}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: errors.city
                        ? '1px solid #dc3545'
                        : '1px solid #ced4da',
                      transition: 'all 0.2s ease',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                    }}
                  >
                    {user.city}
                  </div>
                ),
              )}
            </div>
            <div style={{ flex: 1 }}>
              {renderFieldWithIndicator(
                'street',
                'רחוב',
                isEditMode ? (
                  <InputText
                    value={formData.street}
                    onChange={(e) =>
                      handleInputChange('street', e.target.value)
                    }
                    className={errors.street ? 'p-invalid' : ''}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: errors.street
                        ? '1px solid #dc3545'
                        : '1px solid #ced4da',
                      transition: 'all 0.2s ease',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                    }}
                  >
                    {user.street}
                  </div>
                ),
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '24px', width: '100%' }}>
            <div style={{ flex: 1 }}>
              {renderFieldWithIndicator(
                'houseNumber',
                'מספר בית',
                isEditMode ? (
                  <InputText
                    value={formData.houseNumber}
                    onChange={(e) =>
                      handleInputChange('houseNumber', e.target.value)
                    }
                    className={errors.houseNumber ? 'p-invalid' : ''}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: errors.houseNumber
                        ? '1px solid #dc3545'
                        : '1px solid #ced4da',
                      transition: 'all 0.2s ease',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                    }}
                  >
                    {user.houseNumber}
                  </div>
                ),
              )}
            </div>
            <div style={{ flex: 1 }}>
              {renderFieldWithIndicator(
                'flatNumber',
                'מספר דירה',
                isEditMode ? (
                  <InputText
                    value={formData.flatNumber}
                    onChange={(e) =>
                      handleInputChange('flatNumber', e.target.value)
                    }
                    className={errors.flatNumber ? 'p-invalid' : ''}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: errors.flatNumber
                        ? '1px solid #dc3545'
                        : '1px solid #ced4da',
                      transition: 'all 0.2s ease',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                    }}
                  >
                    {user.flatNumber}
                  </div>
                ),
              )}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            {renderFieldWithIndicator(
              'postcode',
              'מיקוד',
              isEditMode ? (
                <InputText
                  value={formData.postcode}
                  onChange={(e) =>
                    handleInputChange('postcode', e.target.value)
                  }
                  className={errors.postcode ? 'p-invalid' : ''}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: errors.postcode
                      ? '1px solid #dc3545'
                      : '1px solid #ced4da',
                    transition: 'all 0.2s ease',
                  }}
                />
              ) : (
                <div
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#ffffff',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef',
                  }}
                >
                  {user.postcode}
                </div>
              ),
            )}
          </div>
        </div>
      </div>

      <Divider style={{ margin: '32px 0' }} />

      <div style={{ marginTop: '24px' }}>
        <h3
          style={{
            color: '#212529',
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '20px',
            paddingBottom: '12px',
            borderBottom: '1px solid #e9ecef',
          }}
        >
          פרטי עלייה
        </h3>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '24px',
            backgroundColor: '#f8f9fa',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <div style={{ display: 'flex', gap: '24px' }}>
            <div style={{ flex: 1 }}>
              {renderFieldWithIndicator(
                'immigrateCountry',
                'ארץ עלייה',
                isEditMode ? (
                  <InputText
                    value={formData.immigrateCountry || ''}
                    onChange={(e) =>
                      handleInputChange('immigrateCountry', e.target.value)
                    }
                    className={errors.immigrateCountry ? 'p-invalid' : ''}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: errors.immigrateCountry
                        ? '1px solid #dc3545'
                        : '1px solid #ced4da',
                      transition: 'all 0.2s ease',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                    }}
                  >
                    {user.immigrateCountry || 'לא זמין'}
                  </div>
                ),
              )}
            </div>
            <div style={{ flex: 1 }}>
              {renderFieldWithIndicator(
                'immigrateDate',
                'תאריך עלייה',
                isEditMode ? (
                  <Calendar
                    value={formData.immigrateDate}
                    onChange={(e) =>
                      handleInputChange('immigrateDate', e.value)
                    }
                    dateFormat="dd/mm/yy"
                    className={errors.immigrateDate ? 'p-invalid' : ''}
                    style={{ width: '100%' }}
                    inputStyle={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: errors.immigrateDate
                        ? '1px solid #dc3545'
                        : '1px solid #ced4da',
                      transition: 'all 0.2s ease',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                    }}
                  >
                    {user.immigrateDate
                      ? dateFormat.displayFormatDate(user.immigrateDate)
                      : 'לא זמין'}
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      </div>

      <Divider style={{ margin: '32px 0' }} />

      {displayChangePassword && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            padding: '24px',
          }}
        >
          <ChangePasswordFlow
            userPhoneNumber={user.phoneNumber}
            userEmail={user.email}
          />
          <Button
            label="התנתקות"
            icon="pi pi-sign-out"
            onClick={() => setShowLogoutDialog(true)}
            className="p-button-danger p-button-outlined"
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              transition: 'all 0.2s ease',
            }}
          />
        </div>
      )}

      <Dialog
        header="אישור עדכון פרטים"
        visible={showConfirmationDialog}
        style={{ width: '450px' }}
        footer={confirmationDialogFooter}
        onHide={() => setShowConfirmationDialog(false)}
      >
        <div style={{ marginBottom: '16px' }}>
          <p>האם אתה בטוח שברצונך לעדכן את השדות הבאים?</p>
        </div>
        <ScrollPanel style={{ height: '200px' }}>
          <div style={{ padding: '8px' }}>
            {fieldChanges.map((change, index) => (
              <div
                key={index}
                style={{
                  padding: '8px',
                  marginBottom: '8px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                }}
              >
                <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                  {getFieldLabel(change.field)}
                </div>
                <div style={{ display: 'flex', gap: '8px', color: '#6c757d' }}>
                  <span>מערך:</span>
                  <span>{formatValue(change.originalValue)}</span>
                  <span>→</span>
                  <span>{formatValue(change.newValue)}</span>
                </div>
              </div>
            ))}
          </div>
        </ScrollPanel>
      </Dialog>

      <Dialog
        header="אישור התנתקות"
        visible={showLogoutDialog}
        style={{ width: '450px' }}
        footer={logoutDialogFooter}
        onHide={() => !logoutLoading && setShowLogoutDialog(false)}
        closable={!logoutLoading}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '16px 0',
          }}
        >
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: '2rem', color: '#f59e0b' }}
          />
          <p style={{ margin: 0, fontSize: '1rem', color: '#495057' }}>
            שים לב, הינך עומד לבצע התנתקות מהמערכת, האם אתה בטוח?
          </p>
        </div>
      </Dialog>
    </div>
  );
};

// Helper function to get field labels
const getFieldLabel = (field: keyof UserFormData): string => {
  const labels: Record<keyof UserFormData, string> = {
    firstName: 'שם פרטי',
    lastName: 'שם משפחה',
    nationalId: 'תעודת זהות',
    dateOfBirth: 'תאריך לידה',
    gender: 'מגדר',
    maritalStatus: 'מצב משפחתי',
    phoneNumber: 'טלפון',
    email: 'דוא"ל',
    city: 'עיר',
    street: 'רחוב',
    houseNumber: 'מספר בית',
    flatNumber: 'מספר דירה',
    postcode: 'מיקוד',
    immigrateCountry: 'ארץ עלייה',
    immigrateDate: 'תאריך עלייה',
  };
  return labels[field];
};

// Helper function to format values for display
const formatValue = (value: any): string => {
  if (value === null || value === undefined) return 'לא זמין';
  if (value instanceof Date) return dateFormat.displayFormatDate(value);
  return String(value);
};
